import { normalizeAddress } from './chunk-V7TVGBSG.js';
import { toCanonicalChainId } from './chunk-WUQGV726.js';
import { sha256Hex } from './chunk-QXYP3KHC.js';

// src/keys/index.ts
var KEY_DELIMITER = "#";
var MEMPOOL_ID_TAG = "mempool";
var TENANT_PATH_ANY = "*";
var REVERSE_TIME_MAX = (1n << 63n) - 1n;
var RE_EVM_TX_HASH = /^0x[0-9a-fA-F]{64}$/;
var RE_UTXO_TX_HASH = /^[0-9a-fA-F]{64}$/;
function normalizeTxHashForKey(txHash) {
  const text = txHash.trim();
  if (RE_EVM_TX_HASH.test(text) || RE_UTXO_TX_HASH.test(text)) {
    return text.toLowerCase();
  }
  return text;
}
function normaliseSegment(value) {
  if (value == null) return TENANT_PATH_ANY;
  const stripped = value.trim();
  return stripped !== "" ? stripped : TENANT_PATH_ANY;
}
function createKeysWithDigest(sha256Hex2) {
  function _hash(...parts) {
    return sha256Hex2(parts.join("\0"));
  }
  return {
    chainTxKey(chainId, txHash) {
      if (!chainId || !txHash) {
        throw new Error(`chainTxKey requires non-empty chainId and txHash`);
      }
      return `${chainId}${KEY_DELIMITER}${normalizeTxHashForKey(txHash)}`;
    },
    chainAddressKey(chainId, address) {
      if (!chainId || !address) {
        throw new Error(`chainAddressKey requires non-empty chainId and address`);
      }
      return `${chainId}${KEY_DELIMITER}${normalizeAddress(address, chainId)}`;
    },
    canonicalTxId(chainId, blockHash, txHash) {
      if (!chainId || !blockHash || !txHash) {
        throw new Error(`canonicalTxId requires non-empty chainId, blockHash, txHash`);
      }
      return _hash(chainId, blockHash, txHash);
    },
    mempoolCanonicalTxId(chainId, txHash) {
      if (!chainId || !txHash) {
        throw new Error(`mempoolCanonicalTxId requires non-empty chainId and txHash`);
      }
      return _hash(chainId, MEMPOOL_ID_TAG, txHash);
    },
    canonicalEventId(canonicalTxIdValue, eventKind, logIndex = null, internalTxIndex = null) {
      if (!canonicalTxIdValue || !eventKind) {
        throw new Error(`canonicalEventId requires non-empty txId and eventKind`);
      }
      return _hash(
        canonicalTxIdValue,
        eventKind,
        logIndex == null ? "" : String(logIndex),
        internalTxIndex == null ? "" : String(internalTxIndex)
      );
    },
    idempotencyKey(stepName, stepVersion, canonicalTxIdValue, ...extra) {
      if (!stepName || !stepVersion || !canonicalTxIdValue) {
        throw new Error(`idempotencyKey requires non-empty stepName, stepVersion, txId`);
      }
      return _hash(stepName, stepVersion, canonicalTxIdValue, ...extra);
    },
    contentHash(content) {
      return sha256Hex2(content);
    },
    deriveNodeId(prefix, ...parts) {
      if (!prefix) throw new Error(`deriveNodeId requires a non-empty prefix (namespace tag)`);
      return _hash(prefix, ...parts);
    },
    tenantPath(customerId, orgId, tenantId) {
      return [
        normaliseSegment(customerId),
        normaliseSegment(orgId),
        normaliseSegment(tenantId)
      ].join(KEY_DELIMITER);
    },
    tenantUserPath(customerId, orgId, tenantId, userId) {
      return [
        this.tenantPath(customerId, orgId, tenantId),
        normaliseSegment(userId)
      ].join(KEY_DELIMITER);
    },
    reverseTimeSegment(timestampNanos) {
      const ts = typeof timestampNanos === "bigint" ? timestampNanos : BigInt(timestampNanos);
      if (ts < 0n) throw new Error(`reverseTimeSegment requires non-negative timestampNanos`);
      if (ts > REVERSE_TIME_MAX) {
        throw new Error(`reverseTimeSegment timestampNanos ${ts} exceeds max ${REVERSE_TIME_MAX}`);
      }
      return (REVERSE_TIME_MAX - ts).toString(16).padStart(16, "0");
    },
    transactionsHeaderPk(customerId, orgId, tenantId, opts = {}) {
      const prefix = opts.pending ? "v2-pending#" : "v2#";
      return `${prefix}${this.tenantPath(customerId, orgId, tenantId)}`;
    },
    transactionsHeaderSk(canonicalTxIdValue, opts = {}) {
      if (!canonicalTxIdValue) throw new Error(`transactionsHeaderSk requires a non-empty canonicalTxId`);
      const tsUnix = opts.blockTimestampUnix && opts.blockTimestampUnix > 0 ? opts.blockTimestampUnix : 0;
      const revTs = this.reverseTimeSegment(BigInt(tsUnix) * 1000000000n);
      return `${revTs}${KEY_DELIMITER}${canonicalTxIdValue}`;
    }
  };
}
var keys = createKeysWithDigest(sha256Hex);
function splitChainKey(key) {
  const text = key.trim();
  const cut = text.indexOf(KEY_DELIMITER);
  if (cut < 0) return { chain: null, value: text };
  return { chain: text.slice(0, cut) || null, value: text.slice(cut + 1) };
}
function nodeId(chain, address) {
  const canonical = toCanonicalChainId(chain) ?? chain.trim();
  return `${canonical}${KEY_DELIMITER}${normalizeAddress(address, canonical)}`;
}
function parseNodeId(id) {
  const { chain, value } = splitChainKey(id);
  return { chain, address: value };
}
function stableHash(input) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

export { KEY_DELIMITER, MEMPOOL_ID_TAG, TENANT_PATH_ANY, createKeysWithDigest, keys, nodeId, parseNodeId, splitChainKey, stableHash };
//# sourceMappingURL=chunk-T6AOCHLC.js.map
//# sourceMappingURL=chunk-T6AOCHLC.js.map